import { IS_IN_IFRAME } from "@src/env";
import { useCallback, useEffect } from "react";
import {
  iframeReadyMessage,
  isParentInitMessage,
  isWhitelistedParentDomain,
} from "../messages";
import { useIframeStore } from "../store";

/**
 * Sets up the initial iframe ↔ parent `postMessage` flow for the embedded Stitch app.
 *
 * When `IS_IN_IFRAME` is true, posts `EL8_STITCH_IFRAME_READY` to `window.parent` once on
 * mount so the host can send initialization data.
 *
 * Registers a `window` `message` listener for `EL8_STITCH_PARENT_INIT` from allowlisted
 * parent origins and writes EHR, patient, and user email into the iframe store.
 * Non-matching messages and non-allowlisted origins are ignored.
 */
export function useIframeInitialization() {
  const { setEhr, setPatient, setUserEmail } = useIframeStore();

  const handleMessage = useCallback((event: MessageEvent) => {
    const hostname = new URL(event.origin).hostname;

    if (!isWhitelistedParentDomain(hostname)) {
      console.error("Invalid parent domain", hostname);
      return;
    }

    if (!isParentInitMessage(event)) return;

    setEhr(event.data.payload.ehr);
    setPatient(event.data.payload.patient);
    setUserEmail(event.data.payload.user_email);
  }, []);

  useEffect(
    function listForParentInitMessage() {
      if (IS_IN_IFRAME) {
        window.addEventListener("message", handleMessage);

        return () => {
          window.removeEventListener("message", handleMessage);
        };
      }

      return function noop() {
        return void 0;
      };
    },
    [handleMessage],
  );

  useEffect(function postIframeReadyMessage() {
    const message = iframeReadyMessage();

    if (IS_IN_IFRAME) {
      window.parent.postMessage(message, "*");
    }
  }, []);
}
